import React, { useEffect, useState } from "react";
import api from "../api";
import {
  HiOutlineCalendarDays,
  HiOutlineChatBubbleLeftRight,
  HiOutlinePaperAirplane,
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle
} from "react-icons/hi2";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#CCFBF1",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626"
};

function Requests({ user }) {
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setDate(today);
  }, []);

  const submitRequest = async () => {
    setMessage("");

    if (!date || !reason.trim()) {
      setMessage("⚠ Please fill all fields");
      return;
    }

    try {
      setSubmitting(true);

      const res = await api.post("/requests", {
        user_id: user.id,
        date,
        reason: reason.trim()
      });

      setMessage(res.data.message ? `✅ ${res.data.message}` : "✅ Request submitted successfully");
      setReason("");
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.details ||
        "Failed to submit request";

      setMessage(`❌ ${errorMsg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const isError =
    message.includes("❌") || message.includes("⚠") || message.toLowerCase().includes("failed");

  return (
    <div style={page}>
      <div style={hero}>
        <div>
          <h2 style={title}>Late Entry Request</h2>
          <p style={subtitle}>
            Need to add production for an older date? Send a request to your manager for approval.
          </p>
        </div>

        <div style={heroBadge}>
          <HiOutlineChatBubbleLeftRight style={{ fontSize: 18 }} />
          <span>User Request Flow</span>
        </div>
      </div>

      <div style={layout}>
        <div style={formCard}>
          <div style={sectionTitleRow}>
            <div style={iconWrap}>
              <HiOutlineCalendarDays />
            </div>
            <div>
              <h3 style={sectionTitle}>Request Form</h3>
              <p style={sectionSubtitle}>
                Submit one request per date with a clear reason.
              </p>
            </div>
          </div>

          <div style={formBody}>
            <label style={label}>Select Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={input}
            />

            <label style={label}>Reason</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why the production entry was missed..."
              style={textarea}
            />

            <div style={buttonArea}>
              <button
                onClick={submitRequest}
                style={{
                  ...submitBtn,
                  opacity: submitting ? 0.75 : 1,
                  cursor: submitting ? "not-allowed" : "pointer"
                }}
                disabled={submitting}
              >
                <HiOutlinePaperAirplane style={{ fontSize: 18 }} />
                {submitting ? "Submitting..." : "Submit Request"}
              </button>

              {message && (
                <div
                  style={{
                    ...messageBox,
                    background: isError ? "#FEF2F2" : "#ECFDF5",
                    color: isError ? theme.danger : theme.success,
                    borderColor: isError ? "#FECACA" : "#BBF7D0"
                  }}
                >
                  {isError ? (
                    <HiOutlineExclamationTriangle style={{ fontSize: 18, flexShrink: 0 }} />
                  ) : (
                    <HiOutlineCheckCircle style={{ fontSize: 18, flexShrink: 0 }} />
                  )}
                  <span>{message}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={infoCard}>
          <div style={sectionTitleRow}>
            <div style={iconWrap}>
              <HiOutlineChatBubbleLeftRight />
            </div>
            <div>
              <h3 style={sectionTitle}>How it works</h3>
              <p style={sectionSubtitle}>
                Quick steps for requesting late-entry access.
              </p>
            </div>
          </div>

          <div style={infoBody}>
            <div style={stepsWrap}>
              <div style={stepCard}>
                <div style={stepNo}>1</div>
                <div>
                  <p style={stepTitle}>Choose the missed date</p>
                  <p style={stepText}>Pick the exact date for which you need entry access.</p>
                </div>
              </div>

              <div style={stepCard}>
                <div style={stepNo}>2</div>
                <div>
                  <p style={stepTitle}>Provide a proper reason</p>
                  <p style={stepText}>Keep it short, clear, and understandable for your manager.</p>
                </div>
              </div>

              <div style={stepCard}>
                <div style={stepNo}>3</div>
                <div>
                  <p style={stepTitle}>Track status in Inbox</p>
                  <p style={stepText}>Once approved, that date becomes available for entry.</p>
                </div>
              </div>
            </div>

            <div style={noteCard}>
              <strong style={{ color: theme.primaryDark, fontSize: "18px" }}>Note:</strong>
              <p style={noteText}>
                Approved requests unlock only the selected date. Pending or declined requests will remain visible in your inbox.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const page = {
  fontFamily: "sans-serif",
  color: theme.text
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  marginBottom: "24px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 700,
  color: theme.text
};

const subtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px",
  lineHeight: 1.6,
  maxWidth: "680px"
};

const heroBadge = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 14px",
  borderRadius: "999px",
  background: theme.surfaceSoft,
  color: theme.primary,
  border: `1px solid ${theme.border}`,
  fontSize: "13px",
  fontWeight: 700
};

const layout = {
  display: "grid",
  gridTemplateColumns: "1.2fr 1fr",
  gap: "24px",
  alignItems: "stretch"
};

const formCard = {
  background: theme.surface,
  borderRadius: "22px",
  padding: "24px",
  boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
  border: "1px solid #F1F5F9",
  display: "flex",
  flexDirection: "column",
  minHeight: "600px"
};

const infoCard = {
  background: theme.surface,
  borderRadius: "22px",
  padding: "24px",
  boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
  border: "1px solid #F1F5F9",
  display: "flex",
  flexDirection: "column",
  minHeight: "600px"
};

const formBody = {
  display: "flex",
  flexDirection: "column",
  flex: 1
};

const infoBody = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  justifyContent: "space-between"
};

const sectionTitleRow = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "20px"
};

const iconWrap = {
  width: "44px",
  height: "44px",
  borderRadius: "14px",
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
  flexShrink: 0
};

const sectionTitle = {
  margin: 0,
  fontSize: "20px",
  fontWeight: 700,
  color: theme.text
};

const sectionSubtitle = {
  margin: "4px 0 0 0",
  fontSize: "13px",
  color: theme.textMuted
};

const label = {
  display: "block",
  marginBottom: "8px",
  marginTop: "16px",
  fontSize: "14px",
  fontWeight: 600,
  color: theme.text
};

const input = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid #D1D5DB",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box",
  background: "#fff"
};

const textarea = {
  width: "100%",
  minHeight: "160px",
  padding: "14px",
  borderRadius: "12px",
  border: "1px solid #D1D5DB",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box",
  resize: "none",
  fontFamily: "inherit",
  lineHeight: 1.6
};

const buttonArea = {
  marginTop: "auto",
  paddingTop: "24px"
};

const submitBtn = {
  width: "100%",
  border: "none",
  borderRadius: "16px",
  padding: "15px 18px",
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#fff",
  fontSize: "15px",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  boxShadow: "0 10px 20px rgba(15,118,110,0.18)"
};

const messageBox = {
  marginTop: "16px",
  padding: "12px 14px",
  borderRadius: "14px",
  border: "1px solid",
  fontWeight: 600,
  fontSize: "14px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  lineHeight: 1.5
};

const stepsWrap = {
  display: "flex",
  flexDirection: "column",
  gap: "18px"
};

const stepCard = {
  display: "flex",
  gap: "16px",
  padding: "18px",
  borderRadius: "20px",
  background: "#F8FAFC",
  border: "1px solid #E2E8F0",
  alignItems: "flex-start"
};

const stepNo = {
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: theme.primary,
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: "20px",
  flexShrink: 0
};

const stepTitle = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 700,
  color: theme.text
};

const stepText = {
  margin: "8px 0 0 0",
  fontSize: "14px",
  color: theme.textMuted,
  lineHeight: 1.6
};

const noteCard = {
  marginTop: "20px",
  borderRadius: "18px",
  padding: "20px",
  background: "#ECFEFF",
  border: `1px solid ${theme.border}`
};

const noteText = {
  margin: "10px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px",
  lineHeight: 1.7
};

export default Requests;